// src/hooks/useUser.js
import React, { createContext, useState, useEffect, useContext } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "services/firebaseConfig";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    let firestoreUnsubscribe = null;

    const authUnsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const userDocRef = doc(db, "users", user.uid);

        firestoreUnsubscribe = onSnapshot(
          userDocRef,
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              const data = docSnapshot.data();
              // Sanitiza e previne mutação do perfil do lado do cliente
              const sanitizedUser = {
                role: typeof data.role === "string" ? data.role : "pending",
                permissions:
                  typeof data.permissions === "object" &&
                  data.permissions !== null
                    ? data.permissions
                    : {},
              };
              setUserProfile(Object.freeze(sanitizedUser));
            } else {
              setUserProfile(null);
            }
            setLoadingUser(false);
          },
          (error) => {
            console.error("Erro ao buscar perfil do usuário:", error);
            setUserProfile(null);
            setLoadingUser(false);
          },
        );
      } else {
        if (firestoreUnsubscribe) {
          firestoreUnsubscribe();
          firestoreUnsubscribe = null;
        }
        setUserProfile(null);
        setLoadingUser(false);
      }
    });

    return () => {
      if (firestoreUnsubscribe) firestoreUnsubscribe();
      authUnsubscribe();
    };
  }, []);

  const defaultPermissions = {
    canViewExcursions: true,
    canViewChales: true,
    canViewDashboard: false,
    canViewEstoque: false,
  };

  const rawPermissions = userProfile
    ? { ...defaultPermissions, ...userProfile.permissions }
    : {
        canViewExcursions: false,
        canViewChales: false,
        canViewDashboard: false,
        canViewEstoque: false,
      };

  // Garante que as permissões serão sempre booleanos estritos
  const permissions = {
    canViewExcursions: !!rawPermissions.canViewExcursions,
    canViewChales: !!rawPermissions.canViewChales,
    canViewDashboard: !!rawPermissions.canViewDashboard,
    canViewEstoque: !!rawPermissions.canViewEstoque,
  };

  const value = {
    userProfile,
    role: userProfile?.role,
    isLoadingUser: loadingUser,
    isAdmin: userProfile?.role === "admin",
    isEditor: userProfile?.role === "editor",
    isViewer: userProfile?.role === "viewer",
    isPending: userProfile?.role === "pending",

    canViewExcursions: permissions.canViewExcursions,
    canViewChales: permissions.canViewChales,
    canViewDashboard: permissions.canViewDashboard,
    canViewEstoque: permissions.canViewEstoque,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser deve ser usado dentro de um UserProvider");
  }
  return context;
};
